// src/components/MuralManagement.js
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import Papa from "papaparse";
import ToggleVisibilityPopup from './ToggleVisibilityPopup';
import './MuralManagement.css';

const getItemStatus = (item) => {
  if (item.archivedAt) return 'arquivado';
  if (item.expiresAt) {
    const ms = item.expiresAt.toMillis ? item.expiresAt.toMillis() : item.expiresAt.seconds * 1000;
    if (ms < Date.now()) return 'expirado';
  }
  if (item.isVisible === false) return 'oculto';
  return 'ativo';
};

const STATUS_LABELS = {
  ativo: 'Ativo',
  oculto: 'Oculto',
  arquivado: 'Arquivado',
  expirado: 'Expirado',
};

const CATEGORY_LABELS = {
  moradia: 'Moradia',
  alimentacao: 'Alimentação',
  trocas: 'Trocas',
  transporte: 'Transporte',
  documentacao: 'Documentação',
  emprego: 'Emprego',
  comunicacao: 'Comunicação',
  treinamento: 'Treinamento',
  saude: 'Saúde',
  outros: 'Outros',
};

const toDateMs = (value) => {
  if (!value) return null;
  if (value.toMillis) return value.toMillis();
  if (value.seconds) return value.seconds * 1000;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const MuralManagement = () => {
  const [muralItems, setMuralItems] = useState([]);
  const [editItemId, setEditItemId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [selectedItemForVisibility, setSelectedItemForVisibility] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ativo');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  useEffect(() => {
    const fetchMuralItems = async () => {
      const muralCollectionRef = collection(db, "mural");
      const muralData = await getDocs(muralCollectionRef);
      setMuralItems(muralData.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    };

    fetchMuralItems();
  }, []);

  const filteredItems = useMemo(() => {
    const startMs = periodStart ? new Date(`${periodStart}T00:00:00`).getTime() : null;
    const endMs = periodEnd ? new Date(`${periodEnd}T23:59:59`).getTime() : null;
    return muralItems.filter((item) => {
      if (statusFilter !== 'all' && getItemStatus(item) !== statusFilter) return false;
      if (categoryFilter && item.category !== categoryFilter) return false;
      if (typeFilter && item.type !== typeFilter) return false;
      const createdMs = toDateMs(item.createdAt);
      if (startMs !== null && (createdMs === null || createdMs < startMs)) return false;
      if (endMs !== null && (createdMs === null || createdMs > endMs)) return false;
      return true;
    });
  }, [muralItems, statusFilter, categoryFilter, typeFilter, periodStart, periodEnd]);

  const handleArchive = async (item) => {
    const archive = !item.archivedAt;
    const muralDocRef = doc(db, "mural", item.id);
    const adminUid = getAuth().currentUser ? getAuth().currentUser.uid : 'admin';
    try {
      if (archive) {
        await updateDoc(muralDocRef, {
          archivedAt: serverTimestamp(),
          archivedBy: `admin:${adminUid}`,
          isVisible: false,
        });
        setMuralItems(muralItems.map((i) => (i.id === item.id
          ? { ...i, archivedAt: { seconds: Math.floor(Date.now() / 1000) }, archivedBy: `admin:${adminUid}`, isVisible: false }
          : i)));
      } else {
        await updateDoc(muralDocRef, {
          archivedAt: null,
          archivedBy: null,
          isVisible: true,
        });
        setMuralItems(muralItems.map((i) => (i.id === item.id
          ? { ...i, archivedAt: null, archivedBy: null, isVisible: true }
          : i)));
      }
    } catch (error) {
      console.error('Erro ao arquivar item:', error);
    }
  };

  const handleEditClick = (item) => {
    setEditItemId(item.id);
    setEditFormData(item);
  };

  const handleVisibilityClick = (item) => {
    setSelectedItemForVisibility(item);
  };

  const handleVisibilityChange = async (itemId, newVisibility) => {
    try {
      const muralDocRef = doc(db, "mural", itemId);
      await updateDoc(muralDocRef, { isVisible: newVisibility });
      setMuralItems(muralItems.map(item => 
        item.id === itemId ? { ...item, isVisible: newVisibility } : item
      ));
    } catch (error) {
      console.error("Error updating visibility:", error);
    }
  };

  const translateType = (type) => {
    const translations = {
      'offer': 'Oferta',
      'demand': 'Demanda'
    };
    return translations[type] || type;
  };

  const handleUpdate = async () => {
    const muralDocRef = doc(db, "mural", editItemId);
    await updateDoc(muralDocRef, editFormData);
    setMuralItems(muralItems.map(item => (item.id === editItemId ? { ...item, ...editFormData } : item)));
    setEditItemId(null);
  };

  const handleCancelEdit = () => {
    setEditItemId(null);
  };

  const handleDelete = async (itemId) => {
    if (window.confirm("Tem certeza de que deseja excluir este item? Esta ação é irreversível.")) {
      const muralDocRef = doc(db, "mural", itemId);
      await deleteDoc(muralDocRef);
      setMuralItems(muralItems.filter(item => item.id !== itemId));
    }
  };

  const handleFormChange = (event) => {
    setEditFormData({ ...editFormData, [event.target.name]: event.target.value });
  };

  const handleExport = () => {
    const exportData = muralItems.map((item) => {
      console.log('Exporting item:', item); // Debug log
      
      // Formatar data para dd/mm/yyyy
      let formattedDate = '';
      if (item.createdAt) {
        let date;
        // Verificar se é um objeto timestamp do Firestore
        if (item.createdAt.seconds) {
          date = new Date(item.createdAt.seconds * 1000);
        } else {
          date = new Date(item.createdAt);
        }
        
        if (!isNaN(date.getTime())) {
          formattedDate = date.toLocaleDateString('pt-BR');
        }
      }
      
      let expiresFormatted = '';
      if (item.expiresAt) {
        const exp = item.expiresAt.seconds
          ? new Date(item.expiresAt.seconds * 1000)
          : new Date(item.expiresAt);
        if (!isNaN(exp.getTime())) expiresFormatted = exp.toLocaleDateString('pt-BR');
      }

      return {
        Título: item.title || '',
        Descrição: item.description || '',
        Categoria: item.category || '',
        Tipo: item.type === 'demand' ? 'Demanda' : item.type === 'offer' ? 'Oferta' : item.type || '',
        Status: STATUS_LABELS[getItemStatus(item)] || '',
        'Data de Criação': formattedDate,
        'Expira em': expiresFormatted,
      };
    });
    
    console.log('Export data:', exportData); // Debug log
    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "mural_data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="mural-management">
      <h2>Mural Management</h2>
      {editItemId ? (
        <div className="edit-form">
          <label htmlFor="title">Título</label>
          <input
            type="text"
            name="title"
            id="title"
            placeholder="Title"
            value={editFormData.title}
            onChange={handleFormChange}
          />

          <label htmlFor="description">Descrição</label>
          <textarea
            name="description"
            id="description"
            placeholder="Description"
            value={editFormData.description}
            onChange={handleFormChange}
          />

          <label htmlFor="category">Categoria</label>
          <input
            type="text"
            name="category"
            id="category"
            placeholder="Category"
            value={editFormData.category}
            onChange={handleFormChange}
          />

          <label htmlFor="type">Tipo</label>
          <select name="type" id="type" value={editFormData.type} onChange={handleFormChange}>
            <option value="offer">Oferta</option>
            <option value="demand">Demanda</option>
          </select>

          <button onClick={handleUpdate}>Salvar</button>
          <button className="voltar" onClick={handleCancelEdit}>Voltar</button>
        </div>
      ) : (
        <>
          <div className="controls" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="export-button" onClick={handleExport}>
              Exportar mural
            </button>
            <label>
              Status:&nbsp;
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="ativo">Ativos</option>
                <option value="oculto">Ocultos</option>
                <option value="arquivado">Arquivados</option>
                <option value="expirado">Expirados</option>
                <option value="all">Todos</option>
              </select>
            </label>
            <label>
              Categoria:&nbsp;
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="">Todas</option>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label>
              Tipo:&nbsp;
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">Todos</option>
                <option value="offer">Oferta</option>
                <option value="demand">Demanda</option>
              </select>
            </label>
            <label>
              De:&nbsp;
              <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            </label>
            <label>
              Até:&nbsp;
              <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            </label>
          </div>
          <table>
            <thead>
              <tr>
                <th>Título</th>
                <th>Descrição</th>
                <th>Categoria</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Data de Cadastro</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => {
                // Formatar data para dd/mm/yyyy
                let formattedDate = '';
                if (item.createdAt) {
                  let date;
                  // Verificar se é um objeto timestamp do Firestore
                  if (item.createdAt.seconds) {
                    date = new Date(item.createdAt.seconds * 1000);
                  } else {
                    date = new Date(item.createdAt);
                  }
                  
                  if (!isNaN(date.getTime())) {
                    formattedDate = date.toLocaleDateString('pt-BR');
                  }
                }
                
                const status = getItemStatus(item);
                return (
                  <tr key={item.id}>
                    <td>{item.title}</td>
                    <td>{item.description}</td>
                    <td>{item.category}</td>
                    <td>{translateType(item.type)}</td>
                    <td>{STATUS_LABELS[status] || status}</td>
                    <td>{formattedDate || '-'}</td>
                    <td>
                      <div className="actions-column">
                        <button onClick={() => handleVisibilityClick(item)}>
                          {item.isVisible ? 'Ocultar' : 'Tornar visível'}
                        </button>
                        <button onClick={() => handleArchive(item)}>
                          {item.archivedAt ? 'Desarquivar' : 'Arquivar'}
                        </button>
                        <button onClick={() => handleEditClick(item)}>Editar</button>
                        <button className="excluir" onClick={() => handleDelete(item.id)}>Excluir</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {selectedItemForVisibility && (
        <ToggleVisibilityPopup
          item={selectedItemForVisibility}
          onClose={() => setSelectedItemForVisibility(null)}
          onVisibilityChange={handleVisibilityChange}
        />
      )}
    </div>
  );
};

export default MuralManagement;
